import { z } from 'zod';
import { CalendarDate, WorkflowCategory } from './project.js';
/**
 * Hạ tầng báo cáo — APM-801, spec mục 16.
 *
 * ## Ba tính chất bắt buộc của mọi chart (docs/frontend-guide.md §4)
 *
 * 1. **`asOf`** — vẽ được state ở một thời điểm quá khứ. Thiếu nó thì Replay
 *    (S9) phải viết lại toàn bộ tầng chart.
 * 2. **Drill-down** — click vào một phần của biểu đồ ra danh sách task đứng
 *    sau. Vì vậy mọi con số ở đây đều **truy được về task**, không có ô nào là
 *    tổng hợp mù.
 * 3. **Màu RAG dùng token chung** — thuộc tầng giao diện, không có trong
 *    schema này.
 *
 * ## Vì sao dữ liệu chart đến từ `event_log`, không từ bảng `task`
 *
 * Bảng `task` chỉ biết **hiện tại**. "Ngày 12/09 có bao nhiêu task đang làm"
 * không đọc được từ đó — nó là câu hỏi về quá khứ, và câu trả lời nằm ở dòng
 * event. Đây cũng là lý do `asOf` không phải một bộ lọc thêm vào sau: nó là
 * *giới hạn trên của lượt quét event*, tức là chính cách dữ liệu được dựng.
 */
/** Một mốc trên trục ngày của biểu đồ luồng công việc. */
export const FlowBucket = z.object({
    date: CalendarDate,
    /** Số task ở mỗi nhóm trạng thái tại **cuối ngày** đó. */
    todo: z.number().int().nonnegative(),
    inProgress: z.number().int().nonnegative(),
    done: z.number().int().nonnegative(),
    /**
     * Tổng scope tại mốc đó. Không phải `todo + inProgress + done` cho vui: nó là
     * **đường scope** của burnup (spec 16.2), và nhìn nó phình lên giữa sprint là
     * cách duy nhất thấy scope creep trên biểu đồ.
     */
    total: z.number().int().nonnegative(),
});
/** Identifies the exact bounded history used for chart counts and drill-down. */
export const ReportReadSnapshot = z.object({
    revision: z.uuid(),
    cap: z.iso.datetime(),
    computedAt: z.iso.datetime(),
});
export const FlowTimeline = z.object({
    snapshot: ReportReadSnapshot.optional(),
    projectId: z.uuid(),
    from: CalendarDate,
    to: CalendarDate,
    /**
     * Mốc quá khứ đã dùng để dựng dữ liệu. `null` = tới hiện tại.
     *
     * Trả về thay vì để client tự nhớ: một biểu đồ đang xem quá khứ mà không nói
     * ra là một biểu đồ nói dối về hiện tại.
     */
    asOf: z.iso.datetime().nullable(),
    buckets: z.array(FlowBucket),
    /** Số event đã quét để dựng. Cho người đọc biết biểu đồ dựa trên bao nhiêu. */
    eventCount: z.number().int().nonnegative(),
});
/**
 * Loại ô mà người dùng vừa click.
 *
 * Enum chứ không phải chuỗi tự do: mỗi giá trị là một câu truy vấn khác nhau ở
 * `ReportQueryService`, và chuỗi tự do sẽ sinh ra `completedOn`, `completed_on`,
 * `done_at` cho cùng một ý.
 */
export const DrillDownMetric = z.enum([
    /** Task đang ở một nhóm trạng thái vào cuối một ngày. */
    'flow_status',
    /** Task hoàn thành **trong** ngày đó — throughput. */
    'completed_on',
    /** Task được tạo trong ngày đó — đường scope. */
    'created_on',
]);
export const DrillDownItem = z.object({
    taskId: z.uuid(),
    taskKey: z.string(),
    title: z.string(),
    /**
     * ⚠️ Nhãn (tên trạng thái, tên người) là **hiện tại**, còn con số là quá khứ.
     * Đổi tên một cột board không viết lại lịch sử, nên hai thứ này cố tình đến
     * từ hai nguồn khác nhau.
     */
    statusName: z.string().nullable(),
    statusCategory: WorkflowCategory.nullable(),
    assigneeName: z.string().nullable(),
    dueDate: CalendarDate.nullable(),
    estimateHours: z.number().nullable(),
    /** Task đã bị xoá mềm sau thời điểm đang xem — vẫn hiện, nhưng nói rõ. */
    deleted: z.boolean(),
});
export const DrillDownResult = z.object({
    projectId: z.uuid(),
    metric: DrillDownMetric,
    /** Ngày của ô vừa click. */
    bucket: CalendarDate,
    /** Nhóm trạng thái, chỉ có nghĩa với `flow_status`. */
    category: WorkflowCategory.nullable(),
    items: z.array(DrillDownItem),
    /** Tổng số trước khi cắt — hiện "còn N task nữa" thay vì im lặng. */
    totalCount: z.number().int().nonnegative(),
});
/** Trần số item trả về cho một lần drill-down. */
export const DRILL_DOWN_LIMIT = 100;
// ── Flow metrics — APM-802, spec 16.2 ───────────────────────────────────────
/**
 * Một task đã hoàn thành, với ba con số mà spec 16.2 đòi.
 *
 * Ba con số này **không** thay thế được nhau:
 *
 * - `cycleTimeDays` — từ lúc đội bắt đầu làm tới lúc xong. Dùng để dự báo.
 * - `leadTimeDays` — từ lúc yêu cầu được ghi nhận tới lúc xong. Đây là con số
 *   người đặt hàng cảm nhận, và nó luôn lớn hơn cycle time đúng bằng thời gian
 *   nằm chờ trong backlog.
 * - `flowEfficiencyPct` — phần trăm thời gian *thực sự đang làm* trong tổng
 *   thời gian trôi qua. Con số này thường thấp đến mức gây sốc, và đó chính là
 *   thông tin: phần còn lại là chờ đợi.
 */
export const CycleTimeItem = z.object({
    taskId: z.uuid(),
    taskKey: z.string(),
    title: z.string(),
    completedOn: CalendarDate,
    cycleTimeDays: z.number().nonnegative(),
    leadTimeDays: z.number().nonnegative(),
    /** `null` khi tổng thời gian bằng 0 — chia cho 0 không phải 0%. */
    flowEfficiencyPct: z.number().min(0).max(100).nullable(),
});
export const Percentiles = z.object({
    p50: z.number().nonnegative(),
    /**
     * Mốc 85 là mốc dùng để hứa. "50% task xong trong 3 ngày" không hứa được với
     * ai; "85% xong trong 9 ngày" thì hứa được.
     */
    p85: z.number().nonnegative(),
});
export const ControlPoint = z.object({
    date: CalendarDate,
    value: z.number().nonnegative(),
    average: z.number().nonnegative(),
    upper: z.number().nonnegative(),
    lower: z.number().nonnegative(),
    /** Ra ngoài dải ±2σ: dấu hiệu quy trình có gì đổi, không phải một ngày xui. */
    outlier: z.boolean(),
});
export const CycleTimeReport = z.object({
    projectId: z.uuid(),
    from: CalendarDate,
    to: CalendarDate,
    asOf: z.iso.datetime().nullable(),
    items: z.array(CycleTimeItem),
    /** `null` khi chưa task nào hoàn thành trong khoảng — không phải 0. */
    cycle: Percentiles.nullable(),
    lead: Percentiles.nullable(),
    controlChart: z.array(ControlPoint),
});
export const AgingItem = z.object({
    taskId: z.uuid(),
    taskKey: z.string(),
    title: z.string(),
    assigneeName: z.string().nullable(),
    statusCategory: WorkflowCategory,
    /** Số ngày đã nằm ở nhóm trạng thái hiện tại. */
    ageDays: z.number().nonnegative(),
    /** Già hơn mốc p85 của cycle time — task này đang bất thường, không chỉ chậm. */
    overThreshold: z.boolean(),
});
export const AgingWipReport = z.object({
    projectId: z.uuid(),
    asOf: z.iso.datetime().nullable(),
    /**
     * Ngưỡng "già" = p85 cycle time của chính dự án này, không phải một con số
     * chọn bừa. Đội làm việc chậm hay nhanh thì ngưỡng tự đi theo.
     */
    thresholdDays: z.number().nonnegative().nullable(),
    items: z.array(AgingItem),
});
export const ThroughputBucket = z.object({
    date: CalendarDate,
    count: z.number().int().nonnegative(),
});
export const ThroughputReport = z.object({
    projectId: z.uuid(),
    from: CalendarDate,
    to: CalendarDate,
    asOf: z.iso.datetime().nullable(),
    bucket: z.enum(['day', 'week']),
    buckets: z.array(ThroughputBucket),
});
export const BurndownBucket = z.object({
    date: CalendarDate,
    /** Số task chưa xong tại cuối ngày — đường burndown. */
    remaining: z.number().int().nonnegative(),
    /** Tổng scope tại cuối ngày — **đường scope**, thứ làm lộ scope creep. */
    total: z.number().int().nonnegative(),
    /** Số task đã xong — đường burnup. */
    done: z.number().int().nonnegative(),
    /** Đường lý tưởng, nội suy thẳng từ scope đầu kỳ về 0. */
    ideal: z.number().nonnegative(),
});
export const BurndownReport = z.object({
    projectId: z.uuid(),
    from: CalendarDate,
    to: CalendarDate,
    asOf: z.iso.datetime().nullable(),
    /** Sprint đang xem, `null` khi xem theo khoảng ngày (dự án Waterfall). */
    sprintId: z.uuid().nullable(),
    sprintName: z.string().nullable(),
    buckets: z.array(BurndownBucket),
});
export const VelocitySprint = z.object({
    sprintId: z.uuid(),
    sequence: z.number().int().positive(),
    name: z.string(),
    /** Story point đã xong. Sprint chưa đóng vẫn hiện, nhưng tách ra. */
    donePoints: z.number().int().nonnegative(),
    totalPoints: z.number().int().nonnegative(),
    taskCount: z.number().int().nonnegative(),
    closed: z.boolean(),
    /**
     * Đội **nhận** bao nhiêu lúc bắt đầu — APM-AGILE-a. `null` = chưa đo.
     *
     * Đặt cạnh `donePoints` chứ không thay nó: velocity trả lời "làm được bao
     * nhiêu", cột này trả lời "hứa bao nhiêu", và chỉ khi có cả hai mới phân biệt
     * được đội nhận 40 làm 40 với đội nhận 80 làm 40 — hai đội mà velocity vẽ ra
     * cùng một cột cao 40.
     */
    committedPoints: z.number().int().nonnegative().nullable(),
});
export const VelocityReport = z.object({
    projectId: z.uuid(),
    sprints: z.array(VelocitySprint),
    /**
     * Trung bình point của các sprint **đã đóng**. `null` khi chưa đủ sprint để
     * nói gì — dự báo bằng một sprint duy nhất là đoán, không phải dự báo.
     */
    averageDonePoints: z.number().nonnegative().nullable(),
    /**
     * Tỉ lệ **làm được ÷ đã nhận** của các sprint đã đóng có đo cam kết —
     * APM-AGILE-a. `null` khi chưa sprint nào có cả hai con số.
     *
     * 1.0 là hứa đúng khả năng. Dưới 1 là hứa quá; trên 1 là hứa dè, và cả hai
     * đều đáng biết. Chỉ tính trên sprint **đã đóng**: một sprint đang chạy dở
     * luôn có tỉ lệ thấp vì nó chưa xong.
     */
    sayDoRatio: z.number().nonnegative().nullable(),
});
// ═══════════════════════════════════════════════════════════════════════════
// APM-803 — Waterfall (spec 16.3) & chart đặc thù (spec 16.4)
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Một mốc trên đường cong S — spec 16.3.
 *
 * ## Đơn vị là **giờ**, không phải tiền
 *
 * EVM sách vở đo bằng tiền. Hệ thống này không có đơn giá, không có bảng lương,
 * và bịa ra một con số tiền từ giờ là tạo một báo cáo tài chính giả. Giờ ước
 * lượng là đơn vị **có thật** trong dữ liệu, và mọi tỉ số (SPI, CPI) đều không
 * đổi khi đổi đơn vị — nên bản chất chỉ số vẫn đúng.
 */
export const EvmBucket = z.object({
    date: CalendarDate,
    /** Planned Value: phần công việc *lẽ ra* đã xong theo kế hoạch, tính tới cuối ngày. */
    pv: z.number().nonnegative(),
    /** Earned Value: giờ ước lượng của những task **đã xong** tính tới cuối ngày. */
    ev: z.number().nonnegative(),
    /** Actual Cost: giờ làm việc thật sự đã bỏ ra (xem `EvmReport.acNote`). */
    ac: z.number().nonnegative(),
});
export const EvmReport = z.object({
    projectId: z.uuid(),
    from: CalendarDate,
    to: CalendarDate,
    asOf: z.iso.datetime().nullable(),
    /** Budget At Completion: tổng giờ ước lượng của toàn bộ scope hiện tại. */
    bac: z.number().nonnegative(),
    buckets: z.array(EvmBucket),
    /**
     * Schedule Performance Index = EV / PV tại mốc cuối kỳ. `null` khi PV = 0 —
     * chưa có gì *lẽ ra* phải xong thì không có tỉ số nào để nói.
     */
    spi: z.number().nonnegative().nullable(),
    /** Cost Performance Index = EV / AC. `null` khi chưa ai bỏ giờ nào ra. */
    cpi: z.number().nonnegative().nullable(),
    /** Schedule Variance = EV − PV. Âm là chậm so với kế hoạch. */
    sv: z.number(),
    /** Cost Variance = EV − AC. Âm là tốn hơn ước lượng. */
    cv: z.number(),
    /** Số task bị bỏ ngoài phép tính vì chưa có ước lượng giờ. */
    unestimatedTaskCount: z.number().int().nonnegative(),
});
export const SlippageBucket = z.object({
    date: CalendarDate,
    /** Tổng số ngày trễ, cộng dồn trên mọi task đang trễ tại mốc đó. */
    slippageDays: z.number().nonnegative(),
    lateTaskCount: z.number().int().nonnegative(),
});
export const SlippageTask = z.object({
    taskId: z.uuid(),
    taskKey: z.string(),
    title: z.string(),
    baselineDueDate: CalendarDate,
    /** Ngày hoàn thành thật. `null` = vẫn chưa xong. */
    completedOn: CalendarDate.nullable(),
    slippageDays: z.number(),
});
export const SlippageReport = z.object({
    projectId: z.uuid(),
    from: CalendarDate,
    to: CalendarDate,
    asOf: z.iso.datetime().nullable(),
    /** `null` = dự án chưa chốt baseline ⇒ không có gì để so, và biểu đồ nói vậy. */
    baselineSetAt: z.iso.datetime().nullable(),
    buckets: z.array(SlippageBucket),
    /** Trễ nhiều nhất, để không phải mò trong danh sách. */
    worst: z.array(SlippageTask),
});
export const PhaseGate = z.object({
    taskId: z.uuid(),
    taskKey: z.string(),
    title: z.string(),
    /** Đã qua cổng tại thời điểm đang xem chưa. */
    passed: z.boolean(),
    passedOn: CalendarDate.nullable(),
    plannedDate: CalendarDate.nullable(),
    baselineDate: CalendarDate.nullable(),
    /** Trễ so với baseline, ngày. Âm = sớm. `null` = chưa có baseline. */
    slippageDays: z.number().nullable(),
    /** Điều kiện qua cổng: những task cổng này phụ thuộc vào. */
    blockerCount: z.number().int().nonnegative(),
    blockersDone: z.number().int().nonnegative(),
});
export const PhaseGateReport = z.object({
    projectId: z.uuid(),
    asOf: z.iso.datetime().nullable(),
    gates: z.array(PhaseGate),
});
/** Một ô của ma trận rủi ro 5×5 — spec 16.4. */
export const RiskCell = z.object({
    probability: z.number().int().min(1).max(5),
    impact: z.number().int().min(1).max(5),
    count: z.number().int().nonnegative(),
    /** Mã hiển thị (`R-3`) của các rủi ro trong ô — drill-down không cần gọi thêm. */
    riskKeys: z.array(z.string()),
});
export const RiskMatrixReport = z.object({
    projectId: z.uuid(),
    asOf: z.iso.datetime().nullable(),
    /** Luôn đủ 25 ô, kể cả ô rỗng: ma trận thiếu ô thì không còn là ma trận. */
    cells: z.array(RiskCell),
    /** Tổng `probability × impact` của các rủi ro còn mở. */
    totalExposure: z.number().int().nonnegative(),
    openCount: z.number().int().nonnegative(),
    /** Rủi ro còn mở nhưng chưa chấm điểm ⇒ không lên được ma trận. */
    unscoredCount: z.number().int().nonnegative(),
});
export const RiskBurndownBucket = z.object({
    date: CalendarDate,
    /** Tổng điểm rủi ro **còn mở** tại cuối ngày. */
    exposure: z.number().int().nonnegative(),
    openCount: z.number().int().nonnegative(),
    /** Đã giảm thiểu / đã đóng — phần đã xử lý xong, vẽ để thấy công sức bỏ ra. */
    retiredCount: z.number().int().nonnegative(),
});
export const RiskBurndownReport = z.object({
    projectId: z.uuid(),
    from: CalendarDate,
    to: CalendarDate,
    asOf: z.iso.datetime().nullable(),
    buckets: z.array(RiskBurndownBucket),
});
export const EstimateAccuracyItem = z.object({
    taskId: z.uuid(),
    taskKey: z.string(),
    title: z.string(),
    assigneeName: z.string().nullable(),
    taskTypeName: z.string(),
    estimateHours: z.number().positive(),
    /** Giờ làm việc thật sự (xem `EstimateAccuracyReport.actualNote`). */
    actualHours: z.number().nonnegative(),
    /** `actual / estimate`. > 1 là lâu hơn ước lượng. */
    ratio: z.number().nonnegative(),
    completedOn: CalendarDate,
    /** Đã từng bị chặn ⇒ chậm có thể do ngoại cảnh, không do năng lực (spec mục 10). */
    wasBlocked: z.boolean(),
});
/** Gộp theo người hoặc theo loại task — spec 16.4 đòi cả hai lát cắt. */
export const EstimateAccuracyGroup = z.object({
    label: z.string(),
    taskCount: z.number().int().positive(),
    medianRatio: z.number().nonnegative(),
});
export const EstimateAccuracyReport = z.object({
    projectId: z.uuid(),
    from: CalendarDate,
    to: CalendarDate,
    asOf: z.iso.datetime().nullable(),
    items: z.array(EstimateAccuracyItem),
    byAssignee: z.array(EstimateAccuracyGroup),
    byTaskType: z.array(EstimateAccuracyGroup),
    medianRatio: z.number().nonnegative().nullable(),
});
export const ObjectiveTraceNode = z.object({
    objectiveId: z.uuid(),
    title: z.string(),
    taskCount: z.number().int().nonnegative(),
    doneCount: z.number().int().nonnegative(),
    inProgressCount: z.number().int().nonnegative(),
    todoCount: z.number().int().nonnegative(),
});
export const ObjectiveTraceReport = z.object({
    projectId: z.uuid(),
    asOf: z.iso.datetime().nullable(),
    objectives: z.array(ObjectiveTraceNode),
    /**
     * Mục tiêu **không có task nào phục vụ** — spec 16.4 nêu đích danh đây là thứ
     * biểu đồ này phải phát hiện, và nó là con số quan trọng nhất trên màn hình.
     */
    orphanCount: z.number().int().nonnegative(),
    /** Task không phục vụ mục tiêu nào. Nhiều quá thì traceability chỉ là hình thức. */
    unlinkedTaskCount: z.number().int().nonnegative(),
});
export const ChangeRequestTrendBucket = z.object({
    date: CalendarDate,
    created: z.number().int().nonnegative(),
    approved: z.number().int().nonnegative(),
    rejected: z.number().int().nonnegative(),
    applied: z.number().int().nonnegative(),
});
export const ChangeRequestTrendReport = z.object({
    projectId: z.uuid(),
    from: CalendarDate,
    to: CalendarDate,
    asOf: z.iso.datetime().nullable(),
    buckets: z.array(ChangeRequestTrendBucket),
    totalCreated: z.number().int().nonnegative(),
});
export const StaleDocument = z.object({
    documentId: z.uuid(),
    title: z.string(),
    daysWaiting: z.number().int().nonnegative(),
});
export const DocumentStatusReport = z.object({
    projectId: z.uuid(),
    asOf: z.iso.datetime().nullable(),
    draft: z.number().int().nonnegative(),
    review: z.number().int().nonnegative(),
    approved: z.number().int().nonnegative(),
    /** Nằm ở "chờ duyệt" quá lâu — thứ duy nhất trên bảng này cần ai đó ra tay. */
    staleReview: z.array(StaleDocument),
    staleAfterDays: z.number().int().positive(),
});
// ── Nhóm nào hợp với dự án nào ──────────────────────────────────────────────
/**
 * Bốn nhóm biểu đồ — APM-REPORT-b.
 *
 * ## ⚠️ Không nhóm nào mang tên một *phương pháp*
 *
 * Nhóm `schedule` trước đây tên là `waterfall`, và đó là một cái tên sai theo
 * kiểu khó thấy: nó gọi tên **cách làm dự án** thay vì gọi tên **câu hỏi biểu
 * đồ trả lời**. Hậu quả là một dự án Agile có baseline và cổng pha — hoàn toàn
 * hợp lệ — lại thấy một cái nhãn nói rằng phần này không dành cho mình.
 *
 * Bốn nhóm đọc là bốn câu hỏi: dự án đang ổn không (`summary`), việc chảy có
 * trơn không (`flow`), có bám kế hoạch không (`schedule`), có gì đang đe doạ
 * không (`governance`).
 */
/**
 * Nhóm báo cáo — và **thứ tự này là thứ tự hiện trên màn hình**.
 *
 * Trang Báo cáo vẽ mục lục ở cột trái bằng cách duyệt đúng danh sách này, còn
 * nội dung ở cột phải là JSX viết tay. Hai chỗ, một thứ tự — và chúng đã lệch
 * nhau một lần: mục lục để "Rủi ro & quản trị" trên "Vận hành & bảo trì", nội
 * dung thì ngược lại, nên bấm vào mục lục nhảy tới đúng chỗ mà **đọc dọc** lại
 * thấy thứ tự khác (người dùng báo 2026-09-18).
 *
 * `reports-panel.spec` đọc thẳng mã nguồn của panel và so với danh sách này,
 * nên thêm một nhóm mà quên vẽ, hoặc vẽ sai chỗ, là đỏ ngay.
 */
export const ReportGroupKey = z.enum([
    'summary',
    'flow',
    'schedule',
    /** Chỉ dự án bảo trì — APM-MAINT-d. */
    'maintenance',
    /**
     * Vấn đề vận hành — APM-PSET-m.
     *
     * Tách khỏi `maintenance` chứ không nằm trong: hai nhóm đo **hai dòng việc
     * khác nhau**. `maintenance` đo việc đã lên kế hoạch của một dự án chạy vô
     * thời hạn; nhóm này đo sự cố và yêu cầu do người dùng gửi tới — thời gian
     * phản hồi, thời gian xử lý, tồn đọng theo tuổi. Một dự án có thể có dòng này
     * mà không phải dự án bảo trì, và ngược lại.
     */
    'issues',
    'governance',
]);
/**
 * Vì sao một nhóm không có gì để xem.
 *
 * Là **mã**, không phải câu chữ: bản dịch nằm ở frontend (luật i18n). Và có mã
 * riêng cho từng lý do chứ không phải một cờ `available` trần, vì "dự án này
 * chưa chốt baseline" và "dự án này chưa có việc nào" dẫn tới hai hành động
 * khác hẳn nhau.
 */
export const ReportGroupReason = z.enum([
    'no_tasks',
    'no_baseline',
    'no_governance_data',
    /**
     * Dự án bảo trì không có ngày kết thúc — APM-MAINT-d.
     *
     * Khác `no_baseline`: ở đó dữ liệu **chưa có** và chốt baseline là xong. Ở đây
     * nhóm ấy không áp dụng được — burndown, EVM, trượt tiến độ và cổng pha đều
     * đo phần trăm quãng đường đã đi, mà quãng đường này không có đích. Hai câu
     * dẫn tới hai hành động khác nhau, nên hai mã.
     */
    'no_end_date',
    /** Nhóm dành riêng cho dự án bảo trì, dự án khác không có. */
    'not_maintenance',
    /**
     * Chưa ghi nhận vấn đề nào — APM-PSET-m.
     *
     * Khác `no_tasks`: ở đó thiếu **việc của đội**, và cách bù là tạo việc. Ở đây
     * thiếu **vấn đề do người dùng gửi tới**, và không ai tự tạo ra chúng để lấp
     * chỗ trống — một dự án chưa nhận sự cố nào là chuyện tốt, không phải một
     * thiếu sót cần đi sửa.
     */
    'no_issues',
]);
export const ReportGroupAvailability = z.object({
    group: ReportGroupKey,
    available: z.boolean(),
    /** `null` khi `available` — không có gì để giải thích. */
    reason: ReportGroupReason.nullable(),
});
/**
 * Nhóm nào có dữ liệu để xem — **theo dữ liệu, không theo `methodology`**.
 *
 * Khoá cứng theo nhãn phương pháp là cách hỏng im lặng: một dự án Agile vẫn có
 * thể có milestone, baseline và đường găng (`APM-ROLE-r` hiện mốc cho cả hai
 * loại), và khoá theo nhãn sẽ giấu mất đúng những biểu đồ nó cần.
 */
export const ReportAvailability = z.object({
    projectId: z.uuid(),
    groups: z.array(ReportGroupAvailability),
});
/**
 * Tỉ lệ việc phát sinh theo tuần — APM-MAINT-d.
 *
 * Việc phát sinh là việc **không ai kịp lên kế hoạch**: nó được tạo sát ngay
 * lúc phải làm. Suy từ dữ liệu đã có (khoảng cách tạo → phải bắt đầu), không
 * phải một ô ai đó tick — xem `unplanned.ts` ở API để biết ngưỡng và lý do.
 */
export const UnplannedBucket = z.object({
    /** Thứ Hai của tuần, `YYYY-MM-DD`. */
    week: CalendarDate,
    /** Số việc **tính được** thời gian chuẩn bị trong tuần đó. */
    counted: z.number().int().nonnegative(),
    unplanned: z.number().int().nonnegative(),
    /**
     * `unplanned / counted`, hoặc `null` khi tuần đó không có việc nào tính được.
     *
     * `null` chứ không phải 0: "không có việc nào" và "có việc và không cái nào
     * phát sinh" là hai chuyện khác hẳn, và vẽ cả hai thành một điểm 0% là nói
     * dối về tuần đầu.
     */
    ratio: z.number().min(0).max(1).nullable(),
});
export const UnplannedReport = z.object({
    projectId: z.uuid(),
    from: CalendarDate,
    to: CalendarDate,
    asOf: z.iso.datetime().nullable(),
    /** Ngưỡng đang dùng, ngày. Đi ra giao diện để phần "cách đọc" nói đúng số. */
    leadDays: z.number().int().nonnegative(),
    buckets: z.array(UnplannedBucket),
});
/**
 * Năm con số tóm tắt một kỳ vận hành — APM-MAINT-f.
 *
 * Dự án bảo trì không có đường găng, baseline hay ngày kết thúc, nên mọi câu
 * hỏi "còn bao xa nữa" đều vô nghĩa. Câu hỏi thật là **có kham nổi không**, và
 * nó phân rã đúng thành năm con số này — xem `period-stats.ts` để biết vì sao
 * đúng năm con số ấy.
 */
export const PeriodStats = z.object({
    /** Việc **được tạo** trong kỳ. */
    created: z.number().int().nonnegative(),
    /** Việc **được đóng** trong kỳ. */
    completed: z.number().int().nonnegative(),
    /**
     * Việc còn mở ở **thời điểm kỳ khép lại** — không phải ở hôm nay.
     *
     * Dùng trạng thái hôm nay để trả lời một câu hỏi về quá khứ là cách chắc chắn
     * để mọi kỳ cũ trông sạch hơn thực tế nó từng có.
     */
    openAtEnd: z.number().int().nonnegative(),
    /** `null` = chưa có việc nào tính được — khác hẳn `0`. */
    unplannedPct: z.number().int().min(0).max(100).nullable(),
    /** Đã tính tỉ lệ trên bao nhiêu việc; việc không có ngày nào không được tính. */
    unplannedCounted: z.number().int().nonnegative(),
    /** Trung bình số ngày từ lúc bắt đầu tới lúc đóng. `null` = chưa đóng việc nào. */
    avgCycleDays: z.number().nonnegative().nullable(),
});
export const PeriodComparisonReport = z.object({
    projectId: z.uuid(),
    /** Mã kỳ đang xem và kỳ liền trước — `2026-08`, `2026-Q3`, `2026`. */
    period: z.string(),
    previousPeriod: z.string(),
    current: PeriodStats,
    previous: PeriodStats,
});
//# sourceMappingURL=report.js.map